import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';

import LoadingDots from 'src/assets/loading_dots.svg';
import Field from 'src/ConnectedComponents/fields/Field';
import Logo from 'src/ConnectedComponents/utils/Logo';
import LeftArrow from 'src/assets/left_arrow.svg';
import style from './index.css';
import { HyperLink, Span } from 'src/lib/StyledComponents';
import RegionSelect from './RegionSelect';
import CRMSelect from './CRMSelect';
import { STARTER_PACKAGE, OTHER_CRM } from 'src/utils/authOrg.constants';

import {
	requestCreateAppOrganization,
	clearAppOrganizations,
	clearAppOrganizationCreateErrors,
} from 'src/redux/appOrganizations/appOrganization.action';
import {
	getAppOrganizations,
	getAppOrganizationErrors,
	getPendingAppOrganizations,
} from 'src/redux/appOrganizations/appOrganization.selector';
import {
	requestCreateAuthOrganization,
	clearAuthOrganizations,
	clearAuthOrganizationCreateErrors,
} from 'src/redux/authOrganizations/authOrganization.action';
import {
	getAuthOrganizations,
	getAuthOrganizationErrors,
	getPendingAuthOrganizations,
} from 'src/redux/authOrganizations/authOrganization.selector';

class Signup extends Component {
	constructor(props) {
		super(props);
		this.state = {};
	}

	componentDidUpdate(prevProps) {
		if (this.props.authOrganizations.length && !this.state.secondRequest) {
			window.localStorage.wiselyToken = this.props.authOrganizations[0].accessToken;
			this.props.requestCreateAppOrganization({
				name: this.props.authOrganizations[0].name,
				timezone_locale: 'America/Toronto',
				year_start_from: '2020-01-01',
			});
			this.setState({ secondRequest: true });
		}
	}

	handleEmailChange = (_event, value) => {
		this.setState({ email: value });
	};

	handleCountryChange = (event, value) => {
		this.props.clearAuthOrganizationCreateErrors();
		this.props.clearAppOrganizationCreateErrors();
		this.setState({ otherRegion: value === 'Other' });
	};

	handleCRMChange = (_event, value) => {
		this.props.clearAuthOrganizationCreateErrors();
		this.props.clearAppOrganizationCreateErrors();
		this.setState({ otherCRM: value === OTHER_CRM });
	};

	render() {
		if (
			this.props.appOrganizations.length &&
			this.props.authOrganizations.length &&
			this.props.authOrganizations[0].accessToken
		) {
			return <Redirect to={{ pathname: '/' }} />;
		}

		if (this.props.appOrgPending.create || this.props.authOrgPending.create) {
			return (
				<div className={style.CenterContent}>
					<LoadingDots style={{ height: '50px' }} />
				</div>
			);
		}

		const authError = this.props.authOrgErrors.create
			? this.props.authOrgErrors.create.message || this.props.authOrgErrors.create.toString()
			: null;

		const isWiselyDemoUser = ['fundraisewisely', 'wisely.dev'].some(
			str => Boolean(this.state.email) && this.state.email.includes(str),
		);

		return (
			<form onSubmit={this.props.handleSubmit} name="signup" className={style.CenterContent}>
				<Logo />
				<div className={style.SignupPanel}>
					<div className={style.Title}>
						<HyperLink className={style.BackLink} href="https://fundraisewisely.com/">
							<LeftArrow />
						</HyperLink>
						<Span flex="1">Sign up for Wisely</Span>
					</div>
					{this.props.appOrgErrors.create && (
						<div className={style.Error}>
							{this.props.appOrgErrors.create.message
								? this.props.appOrgErrors.create.message
								: this.props.appOrgErrors.create.toString()}
						</div>
					)}
					{authError && (
						<div className={style.Error}>
							{authError}
							{authError.match(/is taken/) ? (
								<Span display="block">
									If you made that account, please{' '}
									<HyperLink href={'/login'}>log in</HyperLink>
								</Span>
							) : null}
						</div>
					)}
					{this.props.error && <div className={style.Error}>{this.props.error}</div>}
					{this.state.otherRegion && (
						<div className={style.Error}>
							Accounts can only be made in Canada and the USA.
						</div>
					)}
					<Field
						name="name"
						placeholder="Organization Name"
						component="input"
						className={style.Input}
					/>
					<Field
						name="fullname"
						placeholder="Your Name"
						component="input"
						className={style.Input}
					/>
					<Field
						name="email"
						placeholder="Email"
						component="input"
						className={style.Input}
						onChange={this.handleEmailChange}
					/>
					<Field
						name="password"
						placeholder="Password"
						component="input"
						type="password"
						autoComplete="new-password"
						className={style.Input}
					/>
					<Field
						name="crm_type"
						component={CRMSelect}
						onChange={this.handleCRMChange}
						isWiselyDemoUser={isWiselyDemoUser}
					/>
					{this.state.otherCRM ? (
						<Field
							name="other_crm"
							placeholder="Enter your CRM"
							component="input"
							className={style.Input}
						/>
					) : null}
					<Field
						name="region"
						placeholder="Country"
						component={RegionSelect}
						onChange={this.handleCountryChange}
					/>
					<div className={style.ButtonContainer}>
						<button className={style.SignupButton} disabled={this.state.otherRegion}>
							Create Account
						</button>
					</div>
				</div>
			</form>
		);
	}
}

const onSubmit = async (org, dispatch, props) => {
	await props.clearAuthOrganizationCreateErrors();
	await props.clearAppOrganizationCreateErrors();

	const region = {
		can: 'can',
		canada: 'can',
		usa: 'usa',
		us: 'usa',
		america: 'usa',
		'united states': 'usa',
		'united states of america': 'usa',
		dev: 'dev',
		staging: 'staging',
		other: 'other',
	}[org.region ? org.region.toLowerCase() : ''];
	org.region = region;

	if (org.crm_type !== OTHER_CRM && org.hasOwnProperty('other_crm')) {
		delete org.other_crm;
	}

	if (!org.name) {
		throw new SubmissionError({
			_error: 'Please enter an Organization Name',
		});
	}

	if (!org.fullname) {
		throw new SubmissionError({
			_error: 'Please enter Your Name',
		});
	}

	if (!org.email) {
		throw new SubmissionError({
			_error: 'Please enter an Email',
		});
	}

	if (!org.password) {
		throw new SubmissionError({
			_error: 'Please enter a Password',
		});
	}

	if (!org.crm_type) {
		throw new SubmissionError({
			_error: 'Please enter a CRM',
		});
	}

	if (!region) {
		throw new SubmissionError({
			_error: 'Please enter a Country',
		});
	}

	if (region === 'other') {
		throw new SubmissionError({
			_error: "Sorry, we can't create an account outside of Canada or the US yet.",
		});
	}

	await props.clearAuthOrganizations();
	await props.clearAppOrganizations();
	await props.requestCreateAuthOrganization({
		...org,
		create_user: true,
		feature_package: STARTER_PACKAGE,
	});
};

const mapStateToProps = state => ({
	appOrganizations: getAppOrganizations(state),
	appOrgErrors: getAppOrganizationErrors(state),
	appOrgPending: getPendingAppOrganizations(state),
	authOrganizations: getAuthOrganizations(state),
	authOrgErrors: getAuthOrganizationErrors(state),
	authOrgPending: getPendingAuthOrganizations(state),
});

const actions = {
	requestCreateAppOrganization,
	clearAppOrganizations,
	clearAppOrganizationCreateErrors,
	requestCreateAuthOrganization,
	clearAuthOrganizations,
	clearAuthOrganizationCreateErrors,
};

Signup = reduxForm({
	form: 'signup',
	onSubmit,
})(Signup);

Signup = connect(mapStateToProps, actions)(Signup);

export default Signup;



// WEBPACK FOOTER //
// ./src/ConnectedComponents/Signup/index.js